// Level-up draft: builds the pool of available upgrades and rolls N distinct choices.
// Choice kinds: new weapon, weapon level-up (faster/+proj/+dmg/...), new passive,
// passive level-up, or a heal fallback when nothing else is available (plan §3.13).

import { DRAFT } from './balance';
import type { Rng } from '../core/rng';
import { PASSIVE_DEFS } from './passives';
import { ELEMENT_COLOR, REACTION_RECIPES, REACTION_REQ, WEAPON_DEFS } from './weapons';
import type { WeaponSystem } from './weapons';

export type DraftKind = 'weapon-new' | 'weapon-up' | 'passive-new' | 'passive-up' | 'reaction' | 'heal';

// Reaction weapons are only obtainable via the reaction recipe, never as a random "new" pick.
const REACTION_KEYS = new Set(REACTION_RECIPES.map((r) => r.result));

export interface DraftChoice {
  kind: DraftKind;
  key: string;
  title: string;
  desc: string;
  color: number;
  level: number; // resulting level (1 for new)
}

const PASSIVE_COLOR = 0xc8b6ff;
const HEAL_COLOR = 0x59ffd0;

// Summarize what a weapon level-up improves, in the player's terms.
function weaponUpDesc(defKey: string, level: number): string {
  const def = WEAPON_DEFS[defKey];
  const a = def.stats(level);
  const b = def.stats(level + 1);
  const parts: string[] = [];
  if (b.damage > a.damage) parts.push(`+${Math.round(b.damage - a.damage)} dmg`);
  if (b.cooldownSec < a.cooldownSec) parts.push('faster firing');
  if (b.count > a.count) parts.push(`+${b.count - a.count} projectile`);
  if (b.pierce > a.pierce) parts.push('+pierce');
  if (b.radius > a.radius + 0.01 || b.range > a.range + 0.01) parts.push('+area');
  return parts.length ? parts.join(', ') : def.desc;
}

export function rollDraft(
  rng: Rng,
  weapons: WeaponSystem,
  passiveLevels: ReadonlyMap<string, number>,
  allowedWeaponKeys?: ReadonlySet<string>,
  allowedPassiveKeys?: ReadonlySet<string>,
): DraftChoice[] {
  const pool: DraftChoice[] = [];

  // Weapons.
  for (const key in WEAPON_DEFS) {
    const def = WEAPON_DEFS[key];
    if (allowedWeaponKeys !== undefined && !allowedWeaponKeys.has(key)) continue;
    if (weapons.has(key)) {
      const lvl = weapons.getLevel(key);
      if (lvl < def.maxLevel) {
        pool.push({
          kind: 'weapon-up',
          key,
          title: `${def.name} Lv ${lvl + 1}`,
          desc: weaponUpDesc(key, lvl),
          color: def.color ?? ELEMENT_COLOR[def.element],
          level: lvl + 1,
        });
      }
    } else if (!REACTION_KEYS.has(key) && weapons.ownedCount < DRAFT.maxWeapons) {
      pool.push({
        kind: 'weapon-new',
        key,
        title: `${def.name} (new)`,
        desc: def.desc,
        color: def.color ?? ELEMENT_COLOR[def.element],
        level: 1,
      });
    }
  }

  // Passives.
  let ownedPassives = 0;
  for (const [, lvl] of passiveLevels) if (lvl > 0) ownedPassives++;
  for (const key in PASSIVE_DEFS) {
    const def = PASSIVE_DEFS[key];
    if (allowedPassiveKeys !== undefined && !allowedPassiveKeys.has(key)) continue;
    const lvl = passiveLevels.get(key) ?? 0;
    if (lvl > 0) {
      if (lvl < def.maxLevel) {
        pool.push({
          kind: 'passive-up',
          key,
          title: `${def.name} Lv ${lvl + 1}`,
          desc: def.desc,
          color: def.color ?? PASSIVE_COLOR,
          level: lvl + 1,
        });
      }
    } else if (ownedPassives < DRAFT.maxPassives) {
      pool.push({
        kind: 'passive-new',
        key,
        title: `${def.name} (new)`,
        desc: def.desc,
        color: def.color ?? PASSIVE_COLOR,
        level: 1,
      });
    }
  }

  // Eligible reactions (the build payoff): both ingredients owned at REACTION_REQ, result not owned.
  const reactions: DraftChoice[] = [];
  for (const r of REACTION_RECIPES) {
    if (
      (allowedWeaponKeys === undefined || allowedWeaponKeys.has(r.result)) &&
      !weapons.has(r.result) &&
      weapons.getLevel(r.a) >= REACTION_REQ &&
      weapons.getLevel(r.b) >= REACTION_REQ
    ) {
      const def = WEAPON_DEFS[r.result];
      reactions.push({
        kind: 'reaction',
        key: r.result,
        title: `⚛ ${def.name}`,
        desc: def.desc,
        color: def.color ?? ELEMENT_COLOR[def.element],
        level: 1,
      });
    }
  }

  // Fisher-Yates shuffle (seeded) the normal pool.
  for (let i = pool.length - 1; i > 0; i--) {
    const j = rng.int(0, i + 1);
    const t = pool[i];
    pool[i] = pool[j];
    pool[j] = t;
  }

  // Guarantee one eligible reaction appears (it's the highlight); fill the rest from the pool.
  const choices: DraftChoice[] = [];
  if (reactions.length > 0) choices.push(reactions[rng.int(0, reactions.length)]);
  for (let i = 0; i < pool.length && choices.length < DRAFT.choices; i++) choices.push(pool[i]);

  // Fallback so the player always has DRAFT.choices options.
  while (choices.length < DRAFT.choices) {
    choices.push({
      kind: 'heal',
      key: 'heal',
      title: 'Elixir of Mending',
      desc: 'Restore 30 HP',
      color: HEAL_COLOR,
      level: 0,
    });
  }
  return choices;
}
